import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { Button } from "@/ds/shadcn/components/ui/button";
import { LoadingState } from "@/ds/custom/LoadingState";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { TableCell } from "@/ds/shadcn/components/ui/table";
import { ListItem } from "@/features/common/components/dataview/ListItem";
import { DataView } from "@/features/common/components/dataview/DataView";
import { GalleryItem } from "@/features/common/components/dataview/GalleryItem";
import { SearchBar } from "@/features/common/components/SearchBar";
import { SearchResults } from "@/features/common/components/SearchResults";
import { SmallProductDisplay } from "@/features/common/components/SmallProductDisplay";
import { useProduct, type GetProductAndVariantsMarketPriceResponseModel, type VariantMarketPriceResponseModel } from "@/features/products/api/useProduct";
import { useProductsSearch, type SearchProductsResponseModel } from "@/features/products/api/useProductSearch";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";

export interface VariantSelectorProps {
	onSelect: (product: GetProductAndVariantsMarketPriceResponseModel, variant: VariantMarketPriceResponseModel) => void;
	onCancel: () => void;
	scrollRef?: React.RefObject<HTMLDivElement | null>;
}

export const VariantSelector: FC<VariantSelectorProps> = props => {
	const {
		onSelect,
		onCancel,
		scrollRef
	} = props;

	const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
	const onVariantCancel = useCallback(() => setSelectedProductId(null), []);
	const onSelectEnriched = useCallback((product: GetProductAndVariantsMarketPriceResponseModel, variant: VariantMarketPriceResponseModel) => {
		onSelect(product, variant);
		setSelectedProductId(null);
	}, [onSelect]);

	return selectedProductId
		? <VariantSelectorVariant productId={selectedProductId} onSelect={onSelectEnriched} onCancel={onCancel} onVariantCancel={onVariantCancel} />
		: <VariantSelectorProduct onSelect={setSelectedProductId} onCancel={onCancel} scrollRef={scrollRef} />;
};

type VariantSelectorVariantProps = Omit<VariantSelectorProps, "scrollRef"> & {
	productId: string;
	onVariantCancel: () => void;
};

const VariantSelectorVariant: FC<VariantSelectorVariantProps> = props => {
	const {
		productId,
		onSelect,
		onCancel,
		onVariantCancel
	} = props;

	const product = useProduct(productId);
	const variants = useMemo(() => product.data?.variants || [], [product.data]);
	const additionalProps = useMemo(() => !product.data ? null : ({
		onSelect: (variant: VariantMarketPriceResponseModel) => {
			onSelect(product.data, variant);
			onCancel();
		},
		imageUrl: product.data.imageUrl
	}), [onSelect, onCancel, product.data]);

	useEffect(() => {
		if (!product.data || variants.length !== 1) {
			return;
		}

		onSelect(product.data, variants[0]);
		onCancel();
	}, [onSelect, onCancel, product.data, variants]);

	if (product.isError) {
		return (
			<div className="p-3">
				<ErrorDisplay error={product.error} />
			</div>
		);
	}

	if (!product.data || !additionalProps) {
		return (
			<LoadingState />
		);
	}

	return (
		<div className="border-b">
			<div className="flex border-b p-3 items-center justify-between gap-3">
				<div className="min-w-0">
					<SmallProductDisplay productName={product.data.name} fcId={product.data.number || "Sealed Product"} imageUrl={product.data.imageUrl} />
				</div>
				<Button variant="outline" onClick={onVariantCancel}>
					Change
				</Button>
			</div>
			<div className="border-b p-3 type-description">
				There are multiple variants of this product.  Please select one.
			</div>
			<DataView
				items={variants}
				getKey={x => x.variantId}
				listViewHeaderLabels={["Name", "Price"]}
				listViewHeaderClassNames={["min-w-0", "w-48 @2xl:w-64"]}
				listComponent={VariantSelectorVariantDataViewListItem}
				additionalProps={additionalProps} />
		</div>
	);
};

type VariantSelectorVariantDataViewListItemProps = VariantMarketPriceResponseModel & {
	imageUrl: string;
	onSelect: (variant: VariantMarketPriceResponseModel) => void;
};

const VariantSelectorVariantDataViewListItem: FC<VariantSelectorVariantDataViewListItemProps> = props => {
	const {
		variantName,
		marketPrice,
		onSelect
	} = props;

	return (
		<ListItem onClick={() => onSelect({ ...props })}>
			<TableCell className="truncate">
				{variantName}
			</TableCell>
			<TableCell>
				{
					marketPrice
						? <MoneyDisplay mode="Standard" money={marketPrice} />
						: "No price available"
				}
			</TableCell>
		</ListItem>
	);
};

type VariantSelectorProductProps = Omit<VariantSelectorProps, "onSelect"> & {
	onSelect: (productId: string) => void;
};

const VariantSelectorProduct: FC<VariantSelectorProductProps> = props => {
	const {
		onCancel,
		onSelect,
		scrollRef
	} = props;

	const [searchQuery, setSearchQuery] = useState("");
	const productSearch = useProductsSearch(searchQuery);
	const additionalProps = useMemo(() => ({ onSelect }), [onSelect]);

	return (
		<div className="relative">
			<div className="sticky top-0 left-0 w-full bg-background z-10">
				<SearchBar value={searchQuery} onChange={setSearchQuery} />
			</div>
			<div>
				<SearchResults searchQuery={searchQuery} forQuery={productSearch} onCancel={onCancel}>
					<DataView
						forQuery={productSearch}
						getKey={x => x.productId}
						scrollRef={scrollRef}
						galleryMinWidth={192}
						galleryComponent={SearchResultsDataViewGalleryItem}
						additionalProps={additionalProps} />
				</SearchResults>
			</div>
		</div>
	);
};

interface SearchResultsDataViewProps extends SearchProductsResponseModel {
	onSelect: (variantId: string) => void;
}

const SearchResultsDataViewGalleryItem: FC<SearchResultsDataViewProps> = props => {
	const {
		productId,
		name,
		number,
		imageUrl,
		onSelect
	} = props;

	const onClick = useCallback(() => onSelect(productId), [onSelect, productId]);

	return (
		<GalleryItem title={name} description={number || undefined} imageUrl={imageUrl} onClick={onClick}></GalleryItem>
	);
};
